/**
*
* Recent page
*
*/
import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import helpContext from 'constants/helpContext';
import Sidebar from 'Workspace/components/WorkspaceSidebar';
import WorkspaceHeader from 'Workspace/components/WorkspaceHeader';
import HomeNavigation from 'Workspace/components/HomeNavigation';
import ProjectActivityList from 'Workspace/Projects/components/ProjectActivityList';
import HelpMenu from 'Workspace/components/WorkspaceHelpMenu';
import { selectCurrentMe } from 'data/CurrentUser/selectors';
import { selectWorkspacesList } from 'Workspace/Admin/store/selectors';
import enhanceWithServices from 'hocs/enhanceWithServices';
import gtmService from 'utils/gtm/gtmService';
import { GTM_EVENT_TYPES } from 'utils/gtm/constants';
import {
  PageViewport,
  PageWrapper,
  PageContent,
} from 'components/PageStyles';
import FavoritesLimitModal from 'Workspace/Projects/components/FavoritesLimitModal';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { MAX_FAVORITES_LIMIT } from 'constants/favoritesLimit';
import { getUUIDDate, getUUIDDateTime } from 'utils/decodeUUIDTime';
import {
  PageViewPortWorkspaceRedesign,
  EmptyContentWrapper,
  MessageParagraph,
  EmptyStateWrapper,
  EmptyStateImageWrapper,
  ErrorStateImageWrapper,
  ErrorContentWrapper,
} from './styles';
import messages from './messages';

const RecentPage = ({currentUser, workspaces, projectService, activityService, annotationService, favoritesService, recentService}) => {
  const [activityList, setActivityList] = useState([]);
  const [sortedActivityList, setSortedActivityList] = useState();
  const [favoritesLength, setFavoritesLength] = useState(0);
  const [sortOrder, setSortOrder] = useState('asc');
  const [isFavoritesLimitModalVisible, setFavoritesLimitModalVisible] = useState(false);
  const [loading , setLoading] = useState(true);
  
  useEffect(()=>{
    gtmService.gtmTrackEvent({ eventType: GTM_EVENT_TYPES.PAGEVIEW, payload: { pageName: 'Recent Page' } });
  }, []);

  useEffect(() => {
    async function getUserRecentList() {
      const accountId = currentUser?.accountId;
      return await recentService.getRecentActivityList(accountId);
    }

    async function fetchRecentList() {
      const recentListResponse = await getUserRecentList();
      const recentlyViewedList = recentListResponse?.recentlyViewedList;
      const activityList = [];

      if(!recentlyViewedList || 
        (recentlyViewedList && recentlyViewedList.length === 0)) {
        setLoading(false);
        setActivityList(recentlyViewedList);
        return;
      } 
      recentlyViewedList.forEach( (activity) => {
        const activityId = activity.activityId || activity.rootElementId;
        activityService.getActivity(activityId).then((activityResponse) => {
          projectService.getProject(activity.projectId).then( (projectDetail) => {
            workspaces && workspaces.forEach((workspace) => {
              if(workspace.id === activity.workspaceId) {
                createActivityResponseObject(activityResponse, activity, projectDetail, workspace);
              }
            });
            activityList.push(activityResponse);
            if(activityList.length === recentlyViewedList.length) {
              setActivityList(activityList);
            }
          });
        });
      });
    }

    if(currentUser && workspaces) {
      fetchRecentList();
    }
  }, [currentUser, projectService, activityService, recentService, workspaces]);

  const createActivityResponseObject = (activityResponse, activity, projectDetail, workspace) => {
    const { config: { title } } = activityResponse;
    activityResponse.configFields = {};
    activityResponse.configFields.title = title;
    activityResponse.dateOpened = getUUIDDate(activity.id);
    activityResponse.dateOpenedTime = getUUIDDateTime(activity.id);
    activityResponse.projectName = projectDetail.name;
    activityResponse.projectId = projectDetail.id;
    activityResponse.workspaceName = workspace.name;
    activityResponse.workspaceId = workspace.id;
    activityResponse.isFavourite = true;
  };

  useEffect(() => {
    async function getUserFavouriteList() {
      const accountId = currentUser?.accountId;
      return await favoritesService.getFavouriteActivityList(accountId);
    }

    async function updateFavoritesForActivity() {
      const favouriteActivityList = await getUserFavouriteList();
      activityList.forEach((activity) => {
        const favouriteActivity = favouriteActivityList?.favorites?.find((favourite) => 
          favourite.activityId === activity.activityId || favourite.rootElementId === activity.activityId
        );
        if (favouriteActivity) {
          activity.isFavourite = true;
          activity.rootElementId = favouriteActivity.rootElementId;
          activity.favoriteId = favouriteActivity.id;
        } else {
          activity.isFavourite = false;
        }
      });
      const activityListUpdated = sortedActivityListUtil(activityList, sortOrder);
      setLoading(false);
      setSortedActivityList([...activityListUpdated]);
    }

    if(activityList && activityList.length > 0) {
      updateFavoritesForActivity();
    } else if (activityList && activityList.length === 0) {
      setSortedActivityList([]);
    } else {
      setSortedActivityList(null);
    }
  }, [sortOrder, activityList, currentUser, projectService, favoritesService, sortedActivityListUtil]);
  
  const orderByType = (data = '') => (new Date(data.dateOpenedTime));

  const closeFavoritesLimitModal = () => {
    setFavoritesLimitModalVisible(false);
  };
  
  const sortedActivityListUtil = useCallback((activityList, order) => activityList.sort( 
    (a, b) => {
      if(order === 'desc') {
        return orderByType(a) - orderByType(b);
      } else 
        return orderByType(b) - orderByType(a);
    }), 
  []);

  const handleToggleFavourite = async (activity) => {
    if(activity.isFavourite) {
      await handleRemoveFavorite(activity);
      return;
    } 
    
    await handleCreateFavourite(activity);
  };

  const handleRemoveFavorite = async (activity) => {
    const { accountId } = currentUser;
    const { favoriteId, activityId, rootElementId } = activity;
    const response = favoritesService.removeFavorites(accountId, favoriteId, rootElementId);
    if(response) {
      const activityListUpdated = activityList.map(
        (activity) => {
          if (activity.activityId === activityId) {
            activity.isFavourite = false;
            setFavoritesLength(favoritesLength - 1);
          }
          return activity;
        }
      );
      setActivityList(activityListUpdated);
    }
  };

  const handleCreateFavourite = async (activity) => {
    const { accountId } = currentUser;
    const { type } = activity?.plugin;
    const { activityId, projectId, workspaceId } = activity;

    if (!accountId || !activityId || !type || !projectId || !workspaceId) {
      return;
    }

    if (favoritesLength >= MAX_FAVORITES_LIMIT) {
      setFavoritesLimitModalVisible(true);
      return;
    }

    const response = await favoritesService.addFavourites(accountId, activityId, type, projectId, workspaceId);
    if (response) {
      const activityListUpdated = activityList.map(
        (activity) => {
          if (activity.activityId === activityId) {
            activity.isFavourite = true;
            activity.rootElementId = activityId;
            activity.favoriteId = response.favoriteId;
            setFavoritesLength(favoritesLength + 1);
          }
          return activity;
        }
      );
      setActivityList(activityListUpdated);
    }
  };

  const sortActivityListByDate = () => {
    const updatedSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    const activityListUpdated = sortedActivityListUtil(sortedActivityList, updatedSortOrder);
    const arrayNew = [ ...activityListUpdated];
    setSortedActivityList(arrayNew);
    setSortOrder(updatedSortOrder);
  };

  const recentEmptyParagarph = () => (
    <><MessageParagraph>{intl.formatMessage(messages.recentEmptyMessage1)}</MessageParagraph>
    <MessageParagraph>{intl.formatMessage(messages.recentEmptyMessage2)}</MessageParagraph>
    <MessageParagraph>{intl.formatMessage(messages.recentEmptyMessage3)}</MessageParagraph></>
  );

  const recentErrorParagarph = () => (
    <><MessageParagraph>{intl.formatMessage(messages.recentErrorMessage1)}</MessageParagraph>
    <MessageParagraph>{intl.formatMessage(messages.recentErrorMessage2)}</MessageParagraph>
    </>
  );

  const recentEmptyErrorContainer = (isEmpty) => (
    <EmptyStateWrapper>
      {isEmpty ? 
      <><EmptyStateImageWrapper /><EmptyContentWrapper>
        {recentEmptyParagarph()}
      </EmptyContentWrapper></> : 
      <><ErrorStateImageWrapper /><ErrorContentWrapper>
        {recentErrorParagarph()}
      </ErrorContentWrapper></>}
    </EmptyStateWrapper>
  );

  const recentListContainer = () => (
    <PageContent>
      {sortedActivityList && sortedActivityList.length === 0 && recentEmptyErrorContainer(true)}
      {!sortedActivityList && recentEmptyErrorContainer(false)}
      {sortedActivityList && sortedActivityList.length > 0 &&
        <ProjectActivityList
          activityList={sortedActivityList}
          currentUser={currentUser}
          toggleFavourite={handleToggleFavourite}
          sortActivityListByDate={sortActivityListByDate}
          annotationService={annotationService}
          sortOrder={sortOrder}
          isWorkspaceRedesignPage={true}
          isRecentPage={true}
        />}
    </PageContent>
  );

  return (
    <PageWrapper>
      <Helmet>
        <title>{intl.formatMessage(messages.pageTitle)}</title>
        <meta
          name="description"
          content={intl.formatMessage(messages.pageDescription)}
        />
      </Helmet>
      <Sidebar navigation={<HomeNavigation showHeader selectedKey="recent"/>} ></Sidebar>
      <PageViewport as={PageViewPortWorkspaceRedesign}>
        <WorkspaceHeader title={intl.formatMessage(messages.recent)} workspaceRedesign={true}/>
        {!loading && recentListContainer()}
        {isFavoritesLimitModalVisible &&
        <FavoritesLimitModal
          visible={isFavoritesLimitModalVisible}
          onDismiss={closeFavoritesLimitModal}
        />
        }
      </PageViewport>
      <HelpMenu helpContext={helpContext.HOME}/>
    </PageWrapper>
  );
};

const mapStateToPros = (state) => ({
  currentUser: selectCurrentMe(state),
  workspaces: selectWorkspacesList(state),
});

export default connect(mapStateToPros)(enhanceWithServices(RecentPage));
